import PropTypes from 'prop-types';
import { get, isEqual } from 'lodash-es';
import { Popover } from '@icp/components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  useCanvasRef,
  useEvents,
  useReadonly,
  useSelectedKeyPath,
  useValue,
  useWorkflowTypeNodeMap,
} from '../NodeContextProvider';
import SettingCondition from './SettingCondition';
import SettingForm from './SettingForm';
import InputUI from '../../../InputElement/InputUI';
import { conditionNoNeedValue } from '../utils';

function NodeSettings(props) {
  const { onClose } = props;

  const { t } = useTranslation(['icp-form-renderer']);
  const value = useValue();
  const selectedKeyPath = useSelectedKeyPath();
  const workflowTypeNodeMap = useWorkflowTypeNodeMap();
  const { onUpdateNode } = useEvents();
  const canvasRef = useCanvasRef();
  const readonly = useReadonly();

  const [anchorEl, setAnchorEl] = useState(null);

  const node = get(value, selectedKeyPath);

  useEffect(() => {
    if (node) {
      setAnchorEl(canvasRef.current.querySelector(`[data-id="${node.id}"]`));
    }
  }, [canvasRef, node]);

  if (!node || !anchorEl) {
    return null;
  }

  const nodeType = workflowTypeNodeMap[node.nodeTypeToken];
  const isBranch = !node.nodeTypeToken;

  const handleClose = () => {
    if (isBranch) {
      const filtered = (node.conditions || []).filter((item) => {
        if (conditionNoNeedValue(item.condition)) {
          return item.dataField && item.condition;
        }
        return item.dataField && item.condition && item.value;
      });
      if (!isEqual(filtered, node.conditions)) {
        onUpdateNode([], { ...node, conditions: filtered });
      }
    }
    onClose();
    setAnchorEl(null);
  };

  return (
    <Popover
      open={true}
      placement="right"
      disableEscapeKeyDown={true}
      anchorEl={anchorEl}
      onClose={handleClose}
      className="icp-workflow-settings-popover"
    >
      <div
        key={String(selectedKeyPath)}
        className={clsx('icp-workflow-settings', { 'condition-setting': isBranch })}
      >
        <div className="setting-header">
          {isBranch ? (
            <div>{t('workflow.condition.title')}</div>
          ) : (
            <InputUI
              id="name"
              value={node.name}
              onChange={(newName) => {
                onUpdateNode(['name'], newName);
              }}
              readonly={readonly}
            />
          )}
        </div>
        {isBranch ? (
          <SettingCondition
            data={node}
            onChange={(newData) => {
              onUpdateNode([], newData);
            }}
          />
        ) : (
          <SettingForm
            data={node.properties}
            pbcToken={nodeType.pagePbcToken}
            pageToken={nodeType.pageToken}
            onChange={(newData) => {
              onUpdateNode(['properties'], newData);
            }}
          />
        )}
      </div>
    </Popover>
  );
}

NodeSettings.propTypes = {
  onClose: PropTypes.func,
};

export default NodeSettings;
